import { MenuItem, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useMeasure } from 'react-use';
import { makeStyles } from 'tss-react/mui';
import { notNullOrEmpty } from '../../utils/common';
import { ResponsiveSelect, ResponsiveSelectProps } from '../responsiveSelect';
import { EmobilityEngineConsumptionAndCo2, EmobilityEngineConsumptionAndCo2Props } from './emobilityEngineConsumptionAndCo2';
import { EmobilityEngineMenuItemContent, EmobilityEngineMenuItemContentProps } from './emobilityEngineValueItemContent';
import { fromSize, StylesProps } from './stylesHelpers';

const useStyles = makeStyles<StylesProps>({ name: 'EmobilityEngineSelect' })((theme, props) => ({
	select: {
		width: '100%',
		paddingTop: 0,
		paddingBottom: 0,
		display: 'flex',
		alignItems: 'center',
		'&.MuiSelect-select': {
			height: fromSize({ '0': 56, '275': 83 })(props)
		}
	},
	icon: {
		top: '50%',
		right: fromSize({ '0': 16, '275': 24 })(props)
	},
	menuItem: {
		'&.mobile-screen': {
			paddingRight: 28,
			paddingLeft: 28,
			paddingTop: 2,
			paddingBottom: 2
		},
		'&.desktop-screen': {
			paddingTop: 4,
			paddingBottom: 4,
			paddingRight: fromSize({ 0: 24, 320: 16, 504: 64 })(props)
		}
	}
}));

export type EmobilityEngineSelectItem = {
	id: string;
	image: string;
	title: string;
	subtitle?: string;
	battery?: string;
	range?: string;
	consumption?: string;
	co2?: string;
};

type OmitProps = 'children' | 'onChange' | 'value';

export type EmobilityEngineSelectProps = Omit<ResponsiveSelectProps, OmitProps> & {
	engines: EmobilityEngineSelectItem[];
	dummy: string;
	labelBattery: string;
	labelRange: string;
	labelConsumption: string;
	labelCo2: string
	value: string;
	showConsumptionAndCo2?: boolean;
	onChange: (value: string) => void;
	onItemClick?: (engine: EmobilityEngineSelectItem, index: number) => void;
};

export const EmobilityEngineSelect: React.FC<EmobilityEngineSelectProps> = (props) => {
	const { engines, value, onChange, dummy, labelBattery, labelRange, labelConsumption, labelCo2, showConsumptionAndCo2, onItemClick, classes, dark, ...other } = props;
	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.originalBreakpoints.only('xs'));
	const [selectRef, { width: selectSize }] = useMeasure();
	const { classes: styles, cx } = useStyles({ selectSize }, { props });

	if (!notNullOrEmpty(engines)) {
		return null;
	}

	const createMobileScreenMenuItemProps = (engine: EmobilityEngineSelectItem) => ({
		title: engine.title,
		subtitle: engine.subtitle,
		titleVariant: 'medium',
		image: { src: engine.image, dummy },
		imageVariant: 'small',
		range: { value: engine.range },
		valueVariant: 'large'
	} as EmobilityEngineMenuItemContentProps);

	const createDesktopScreenMenuItemProps = (engine: EmobilityEngineSelectItem) => ({
		title: engine.title,
		subtitle: engine.subtitle,
		titleVariant: 'small',
		valueVariant: fromSize({ '0': 'small', '822': 'small-wide' })({ selectSize }),
		image: fromSize({ '0': null, '275': { src: engine.image, dummy } })({ selectSize }),
		range: fromSize({ '0': null, '320': { label: labelRange, value: engine.range } })({ selectSize }),
		battery: fromSize({ '0': null, '416': { label: labelBattery, value: engine.battery } })({ selectSize })
	} as EmobilityEngineMenuItemContentProps);

	const createMenuItemProps = (engine: EmobilityEngineSelectItem) =>
		isMobileScreen
			? createMobileScreenMenuItemProps(engine)
			: createDesktopScreenMenuItemProps(engine);

	const crateValueProps = (engine: EmobilityEngineSelectItem) => ({
		title: engine.title,
		subtitle: engine.subtitle,
		titleVariant: fromSize({ '0': 'small', '320': 'large' })({ selectSize }),
		valueVariant: fromSize({ '0': 'small', '822': 'small-wide' })({ selectSize }),
		image: fromSize({ '0': null, '275': { src: engine.image, dummy } })({ selectSize }),
		range: fromSize({ '0': null, '416': { label: labelRange, value: engine.range } })({ selectSize }),
		battery: fromSize({ '0': null, '504': { label: labelBattery, value: engine.battery } })({ selectSize })
	} as EmobilityEngineMenuItemContentProps);

	const menuItems = engines.map((engine, index) =>
		<MenuItem
			data-test={`menu-item-${engine.id}`}
			key={engine.id} value={engine.id}
			className={cx(styles.menuItem, isMobileScreen ? 'mobile-screen' : 'desktop-screen')}
			onClick={() => onItemClick?.(engine, index)}>
			<EmobilityEngineMenuItemContent {...createMenuItemProps(engine)} />
		</MenuItem>);

	const renderValue = (id: string) => {
		const engine = engines.find(e => e.id === id);
		const props = crateValueProps(engine);
		return <EmobilityEngineMenuItemContent {...props} />;
	};

	const createConsumptionaAndCo2Props = (engine: EmobilityEngineSelectItem) => ({
		consumption: (showConsumptionAndCo2 && engine.consumption) ? { label: labelConsumption, value: engine.consumption } : null,
		co2: (showConsumptionAndCo2 && engine.co2) ? { label: labelCo2, value: engine.co2 } : null
	} as EmobilityEngineConsumptionAndCo2Props);

	const renderConsumptionaAndCo2 = (id: string) => {
		const engine = engines.find(e => e.id === id);
		const propsConsumptionAndCo2 = createConsumptionaAndCo2Props(engine);
		return <EmobilityEngineConsumptionAndCo2 {...propsConsumptionAndCo2} />;
	};

	const DesktopMenuProps = {
		...theme.components.MuiSelect.defaultProps.MenuProps,
		PaperProps: {
			...theme.components.MuiSelect.defaultProps.MenuProps.PaperProps,
			style: { width: selectSize }
		}
	};

	return (
		<React.Fragment>
			<ResponsiveSelect
				data-test='emobility-engine-select'
				ref={selectRef}
				classes={{
					...classes,
					select: styles.select,
					iconFilled: styles.icon
				}}
				value={value}
				onChange={(ev) => onChange(ev.target.value as string)}
				renderValue={renderValue}
				variant='filled'
				dark={dark}
				MenuProps={isMobileScreen ? undefined : DesktopMenuProps}
				{...other}>
				{menuItems}
			</ResponsiveSelect>
			{
				showConsumptionAndCo2 && value && renderConsumptionaAndCo2(value)
			}
		</React.Fragment>
	);
};

EmobilityEngineSelect.defaultProps = {
	dark: true
};
